import { css } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const IMAGE_CONTAINER_STYLE = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  margin: 0;
`;

const IMAGE_CONTAINER_STYLE_ZOOM_2X = css`
  label: IMAGE_CONTAINER_STYLE_ZOOM_2X;
  overflow: auto;
  align-items: flex-start;
  justify-content: flex-start;
`;

// tweak this to control the "zoomed in" view scale
const ZOOM_SCALE = 5;

const IMAGE_STYLE_FIT = css`
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: zoom-in;
`;

const IMAGE_STYLE_ZOOM_2X = css`
  max-width: none;
  max-height: none;
  object-fit: contain;
  display: block;
  margin: 0;
  cursor: zoom-out;
`;

type ZoomMode = 'fit' | 'zoomed';

type PendingZoomScroll = { left: number; top: number };

const ZoomableImageViewerInner = ({
  imageUri,
  onError,
}: {
  imageUri: string;
  onError?: () => void;
}) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [zoomMode, setZoomMode] = useState<ZoomMode>('fit');
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | undefined>(undefined);
  const [imageContainerSize, setImageContainerSize] = useState<{ width: number; height: number } | undefined>(undefined);
  const pendingZoomScrollRef = useRef<PendingZoomScroll | undefined>(undefined);

  const fitMetrics = useMemo(() => {
    if (!imageNaturalSize || !imageContainerSize) return undefined;
    const fitScale = Math.min(imageContainerSize.width / imageNaturalSize.width, imageContainerSize.height / imageNaturalSize.height);
    const fitWidth = imageNaturalSize.width * fitScale;
    const fitHeight = imageNaturalSize.height * fitScale;
    return {
      fitScale,
      fitOffsetX: (imageContainerSize.width - fitWidth) / 2,
      fitOffsetY: (imageContainerSize.height - fitHeight) / 2,
    };
  }, [imageContainerSize, imageNaturalSize]);

  const zoomedImageSize = useMemo(() => (
    imageNaturalSize && fitMetrics
      ? {
        width: imageNaturalSize.width * fitMetrics.fitScale * ZOOM_SCALE,
        height: imageNaturalSize.height * fitMetrics.fitScale * ZOOM_SCALE,
        fitScale: fitMetrics.fitScale * ZOOM_SCALE,
      }
      : undefined
  ), [fitMetrics, imageNaturalSize]);

  const toggleZoom = useCallback((event?: React.MouseEvent<HTMLImageElement>) => {
    if (zoomMode !== 'fit') {
      pendingZoomScrollRef.current = undefined;
      setZoomMode('fit');
      return;
    }

    const containerElement = imageContainerRef.current;
    if (!containerElement || !imageNaturalSize || !imageContainerSize || !fitMetrics) {
      setZoomMode('zoomed');
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const clickX = event ? Math.min(imageContainerSize.width, Math.max(0, event.clientX - containerRect.left)) : imageContainerSize.width / 2;
    const clickY = event ? Math.min(imageContainerSize.height, Math.max(0, event.clientY - containerRect.top)) : imageContainerSize.height / 2;

    const imageX = Math.min(imageNaturalSize.width, Math.max(0, (clickX - fitMetrics.fitOffsetX) / fitMetrics.fitScale));
    const imageY = Math.min(imageNaturalSize.height, Math.max(0, (clickY - fitMetrics.fitOffsetY) / fitMetrics.fitScale));

    const zoomScale = fitMetrics.fitScale * ZOOM_SCALE;
    pendingZoomScrollRef.current = {
      left: (imageX * zoomScale) - clickX,
      top: (imageY * zoomScale) - clickY,
    };

    setZoomMode('zoomed');

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const pendingZoomScroll = pendingZoomScrollRef.current;
        if (!imageContainerRef.current || !pendingZoomScroll) return;
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } = imageContainerRef.current;
        imageContainerRef.current.scrollLeft = Math.min(scrollWidth - clientWidth, Math.max(0, pendingZoomScroll.left));
        imageContainerRef.current.scrollTop = Math.min(scrollHeight - clientHeight, Math.max(0, pendingZoomScroll.top));
        pendingZoomScrollRef.current = undefined;
      })
    );
  }, [fitMetrics, imageContainerSize, imageNaturalSize, zoomMode]);

  // Track container size
  useEffect(() => {
    if (!imageContainerRef.current) return;
    const updateSize = () =>
      imageContainerRef.current && setImageContainerSize({ width: imageContainerRef.current.clientWidth, height: imageContainerRef.current.clientHeight });
    updateSize();

    const ResizeObserverConstructor = (globalThis as any).ResizeObserver as typeof ResizeObserver | undefined;
    if (!ResizeObserverConstructor) {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const resizeObserver = new ResizeObserverConstructor(() => updateSize());
    resizeObserver.observe(imageContainerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={imageContainerRef} css={[IMAGE_CONTAINER_STYLE, zoomMode === 'zoomed' && IMAGE_CONTAINER_STYLE_ZOOM_2X]}>
      <img
        src={imageUri}
        alt='Full size'
        css={zoomMode === 'fit' ? IMAGE_STYLE_FIT : IMAGE_STYLE_ZOOM_2X}
        onClick={toggleZoom}
        onLoad={event => setImageNaturalSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
        onError={onError}
        style={zoomMode === 'zoomed' && zoomedImageSize ? { width: zoomedImageSize.width, height: zoomedImageSize.height } : undefined}
      />
    </div>
  );
};

// Use key={imageUri} so the inner component remounts when the image changes, resetting zoom state.
// This avoids a useEffect that calls setState on imageUri change, which triggers react-hooks/set-state-in-effect.
export const ZoomableImageViewer = (props: { imageUri: string; onError?: () => void }) => <ZoomableImageViewerInner key={props.imageUri} {...props} />;
